import {useCallback, useEffect, useMemo, useState} from "react";
import type {Editor} from "@tiptap/core";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {NodeSelection} from "@tiptap/pm/state";
import {PRINTOUT_BLOCK_REGISTRY} from "../../blocks";
import type {PrintoutBlockType} from "../../blocks";
import {findPrintoutRegion} from "../../blocks/headerFooter/printoutRegionOptions";
import type {PrintoutRegionKind} from "../../blocks/headerFooter/printoutRegionOptions";

export interface SelectedPrintoutNode {
    node: ProseMirrorNode;
    pos: number;
}

export interface SelectedPrintoutBlock extends SelectedPrintoutNode {
    type: PrintoutBlockType;
}

const PRINTOUT_VARIABLE_NODE_NAME = "printoutVariable";

const PRINTOUT_BLOCK_TYPE_NAMES = new Set<string>(PRINTOUT_BLOCK_REGISTRY.map(block => block.type));

const asPrintoutBlockType = (name: string): PrintoutBlockType | null => (
    PRINTOUT_BLOCK_TYPE_NAMES.has(name) ? name as PrintoutBlockType : null
);

const resolveSelectedPrintoutBlock = (editor: Editor | null): SelectedPrintoutBlock | null => {
    if (!editor || editor.isDestroyed) return null;

    const {selection} = editor.state;

    if (selection instanceof NodeSelection) {
        const type = asPrintoutBlockType(selection.node.type.name);

        if (type) return {node: selection.node, pos: selection.from, type};
    }

    const {$from} = selection;

    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const ancestor = $from.node(depth);
        const type = asPrintoutBlockType(ancestor.type.name);

        if (type) return {node: ancestor, pos: $from.before(depth), type};
    }

    return null;
};

const resolveSelectedPrintoutVariable = (editor: Editor | null): SelectedPrintoutNode | null => {
    if (!editor || editor.isDestroyed) return null;

    const {selection} = editor.state;

    return selection instanceof NodeSelection && selection.node.type.name === PRINTOUT_VARIABLE_NODE_NAME
        ? {node: selection.node, pos: selection.from}
        : null;
};

const useResolvedSelection = <T extends SelectedPrintoutNode>(
    editor: Editor | null,
    resolve: (editor: Editor | null) => T | null,
): T | null => {
    const [selected, setSelected] = useState<T | null>(null);

    useEffect(() => {
        if (!editor) {
            setSelected(null);
            return;
        }

        const sync = () => {
            setSelected(current => {
                const next = resolve(editor);

                if (!current || !next) return next;

                // Node identity detects immutable changes without rerendering on typing noise.
                return current.node === next.node && current.pos === next.pos ? current : next;
            });
        };

        sync();
        editor.on("selectionUpdate", sync);
        editor.on("transaction", sync);

        return () => {
            editor.off("selectionUpdate", sync);
            editor.off("transaction", sync);
        };
    }, [editor, resolve]);

    return selected;
};

export function useSelectedPrintoutBlock(editor: Editor | null): SelectedPrintoutBlock | null {
    return useResolvedSelection(editor, resolveSelectedPrintoutBlock);
}

export function useSelectedPrintoutVariable(editor: Editor | null): SelectedPrintoutNode | null {
    return useResolvedSelection(editor, resolveSelectedPrintoutVariable);
}

export interface PrintoutDocumentRegion extends SelectedPrintoutNode {
    kind: PrintoutRegionKind;
}

export function usePrintoutDocumentRegion(
    editor: Editor | null,
    kind: PrintoutRegionKind,
): PrintoutDocumentRegion | null {
    const resolve = useCallback((instance: Editor | null): PrintoutDocumentRegion | null => {
        if (!instance || instance.isDestroyed) return null;

        const region = findPrintoutRegion(instance.state.doc, kind);

        return region
            ? {kind, node: region.node, pos: region.pos}
            : null;
    }, [kind]);

    return useResolvedSelection(editor, resolve);
}

export function usePrintoutDocumentRegions(editor: Editor | null): {hasFooter: boolean, hasHeader: boolean} {
    const header = usePrintoutDocumentRegion(editor, 'header');
    const footer = usePrintoutDocumentRegion(editor, 'footer');

    return useMemo(() => ({hasFooter: !!footer, hasHeader: !!header}), [footer, header]);
}

export default useSelectedPrintoutBlock;
